import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { AutocompleteInput } from './components/AutocompleteInput';
import { GuessRow } from './components/GuessRow';
import { ShareButton } from './components/ShareButton';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { SkullBackground } from '../components/SkullBackground';
import {
  type BrawdleErrorResponse,
  type BrawdleGameStatus,
  type BrawdleGuessRequest,
  type BrawdleGuessResponse,
  type BrawdleMetaResponse,
  type BrawdleRevealResponse,
  type BrawdleSuggestion,
  type BrawdleStoredState,
} from '../../shared/types/api';
import { BRAWLERS, type Brawler } from '../../shared/data/brawler';
import { useLocaleStrings, type BrawdleStrings } from '../i18n';

type GuessEntry = {
  brawler: Brawler;
  result: BrawdleGuessResponse['result'];
  isCorrect: boolean;
};

type RevealedBrawler = {
  name: string;
  slug: string;
  imagePath?: string | null;
};

type GameShellProps = {
  children: ReactNode;
};

const GameShell = ({ children }: GameShellProps) => (
  <div className="game-page brawlstar-gradient">
    <SkullBackground />
    {children}
  </div>
);

const STORAGE_PREFIX = 'brawdle:';

const createEmptyState = (): BrawdleStoredState => ({
  guesses: [],
  status: 'playing',
});

const findBrawler = (slug: string, fallbackName: string): Brawler | undefined =>
  BRAWLERS.find((brawler) => brawler.slug === slug) ??
  BRAWLERS.find((brawler) => brawler.name === fallbackName) ??
  BRAWLERS.find((brawler) => brawler.key === fallbackName);

export const App = () => {
  const strings = useLocaleStrings();
  const [meta, setMeta] = useState<BrawdleMetaResponse | null>(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [metaError, setMetaError] = useState<string | null>(null);

  const fetchMeta = useCallback(() => {
    setLoadingMeta(true);
    setMetaError(null);
    fetch('/api/brawdle/meta')
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(strings.app.errorLoading);
        }
        return (await response.json()) as BrawdleMetaResponse;
      })
      .then((data) => setMeta(data))
      .catch((error) => {
        console.error(error);
        setMetaError(error instanceof Error ? error.message : strings.app.errorLoading);
      })
      .finally(() => setLoadingMeta(false));
  }, [strings]);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  if (loadingMeta) {
    return (
      <GameShell>
        <div className="brawdle-layout">
          <p className="text-center text-white">{strings.app.loading}</p>
        </div>
      </GameShell>
    );
  }

  if (metaError) {
    return (
      <GameShell>
        <div className="brawdle-layout text-center">
          <p className="mb-4 text-white">{metaError}</p>
          <button
            type="button"
            className="rounded-2xl bg-white/10 px-4 py-2 text-sm font-semibold text-white"
            onClick={fetchMeta}
          >
            {strings.app.retry}
          </button>
        </div>
      </GameShell>
    );
  }

  if (!meta) {
    return null;
  }

  return (
    <GameShell>
      <div className="brawdle-layout">
        <GameBoard meta={meta} strings={strings} />
      </div>
    </GameShell>
  );
};

type GameBoardProps = {
  meta: BrawdleMetaResponse;
  strings: BrawdleStrings;
};

const GameBoard = ({ meta, strings }: GameBoardProps) => {
  const storageKey = `${STORAGE_PREFIX}${meta.puzzleId}`;
  const defaultState = useMemo(createEmptyState, [meta.puzzleId]);
  const [persistedState, setPersistedState, storageReady] = useLocalStorage<BrawdleStoredState>(
    storageKey,
    defaultState
  );

  const [status, setStatus] = useState<BrawdleGameStatus>(persistedState.status);
  const [guesses, setGuesses] = useState<GuessEntry[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<BrawdleSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hydrating, setHydrating] = useState(true);
  const [hasHydrated, setHasHydrated] = useState(false);
  const [revealedBrawler, setRevealedBrawler] = useState<RevealedBrawler | null>(null);
  const [showEndModal, setShowEndModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const fetchReveal = useCallback(async () => {
    try {
      const response = await fetch(`/api/brawdle/reveal?puzzleId=${meta.puzzleId}`);
      if (!response.ok) {
        throw new Error('Reveal error');
      }
      const data = (await response.json()) as BrawdleRevealResponse;
      setRevealedBrawler({
        name: data.canonicalName,
        slug: data.canonicalSlug,
        imagePath: data.imagePath,
      });
    } catch (revealError) {
      console.error('Unable to reveal brawler of the day', revealError);
    }
  }, [meta.puzzleId]);

  const getErrorMessageForCode = (code?: BrawdleErrorResponse['code']) => {
    switch (code) {
      case 'PUZZLE_MISMATCH':
        return strings.errors.puzzleMismatch;
      case 'UNKNOWN_CHARACTER':
        return strings.errors.unknownCharacter;
      case 'INVALID_BODY':
        return strings.errors.invalidBody;
      default:
        return strings.errors.generic;
    }
  };

  useEffect(() => {
    setStatus(defaultState.status);
    setGuesses([]);
    setInputValue('');
    setSelectedSlug(null);
    setRevealedBrawler(null);
    setShowEndModal(false);
    setLinkCopied(false);
    setHasHydrated(false);
    setHydrating(true);
  }, [defaultState]);

  useEffect(() => {
    if (!storageReady) {
      return;
    }

    if (hasHydrated) {
      setHydrating(false);
      setHasHydrated(true);
      return;
    }

    let cancelled = false;
    const replay = async () => {
      if (persistedState.guesses.length === 0) {
        if (!cancelled) {
          setStatus(persistedState.status);
          setHydrating(false);
          setHasHydrated(true);
        }
        return;
      }

      try {
        const restored: GuessEntry[] = [];
        for (const guessSlug of persistedState.guesses) {
          const payload: BrawdleGuessRequest = {
            puzzleId: meta.puzzleId,
            guessSlug,
          };
          const response = await fetch('/api/brawdle/guess', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          if (!response.ok) {
            throw new Error('Unable to restore progress.');
          }
          const data = (await response.json()) as BrawdleGuessResponse;
          const brawler = findBrawler(data.canonicalSlug, data.canonicalName);
          if (brawler) {
            restored.push({ brawler, result: data.result, isCorrect: data.isCorrect });
          }
        }
        if (!cancelled) {
          setGuesses(restored);
          setStatus(persistedState.status);
        }
      } catch (replayError) {
        console.error(replayError);
        if (!cancelled) {
          setGuesses([]);
          setStatus('playing');
          setPersistedState(createEmptyState());
        }
      } finally {
        if (!cancelled) {
          setHydrating(false);
          setHasHydrated(true);
        }
      }
    };

    replay();

    return () => {
      cancelled = true;
    };
  }, [hasHydrated, meta.puzzleId, persistedState, setPersistedState, storageReady]);

  useEffect(() => {
    if (!inputValue.trim()) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const guessedSlugs = new Set(guesses.map((guess) => guess.brawler.slug));

    const controller = new AbortController();
    setSuggestionsLoading(true);
    const debounce = window.setTimeout(async () => {
      try {
        const response = await fetch(
          `/api/brawdle/suggestions?q=${encodeURIComponent(inputValue.trim())}`,
          { signal: controller.signal }
        );
        if (!response.ok) {
          throw new Error('Suggestions error');
        }
        const data = (await response.json()) as { suggestions: BrawdleSuggestion[] };
        const filtered = data.suggestions.filter(
          (suggestion) => !suggestion.slug || !guessedSlugs.has(suggestion.slug)
        );
        setSuggestions(filtered);
      } catch (suggestionError) {
        if (suggestionError instanceof DOMException && suggestionError.name === 'AbortError') {
          return;
        }
        console.error(suggestionError);
      } finally {
        setSuggestionsLoading(false);
      }
    }, 200);

    return () => {
      controller.abort();
      clearTimeout(debounce);
    };
  }, [guesses, inputValue]);

  const remainingAttempts = meta.maxGuesses - guesses.length;
  const inputDisabled =
    !storageReady || hydrating || submitting || status !== 'playing' || guesses.length >= meta.maxGuesses;
  const submitLabel = submitting ? strings.form.submitting : strings.form.submit;

  const handleSubmit = async (override?: { slug: string; name: string }) => {
    if (inputDisabled && !override) {
      return;
    }

    const guessSlug = (override?.slug ?? selectedSlug ?? inputValue).trim();
    if (!guessSlug) {
      setError(strings.form.missingInput);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload: BrawdleGuessRequest = {
        puzzleId: meta.puzzleId,
        guessSlug,
      };
      const response = await fetch('/api/brawdle/guess', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const payloadError = (await response.json().catch(() => null)) as BrawdleErrorResponse | null;
        const message = getErrorMessageForCode(payloadError?.code);
        throw new Error(message);
      }
      const data = (await response.json()) as BrawdleGuessResponse;
      const brawler = findBrawler(data.canonicalSlug, data.canonicalName);
      if (!brawler) {
        throw new Error(strings.errors.generic);
      }
      const nextGuesses = [...guesses, { brawler, result: data.result, isCorrect: data.isCorrect }];
      setGuesses(nextGuesses);

      const nextStatus: BrawdleGameStatus = data.isCorrect
        ? 'won'
        : nextGuesses.length >= meta.maxGuesses
        ? 'lost'
        : 'playing';
      setStatus(nextStatus);
      setPersistedState((prev) => ({
        guesses: [...prev.guesses, brawler.slug],
        status: nextStatus,
      }));
      if (data.isCorrect) {
        setRevealedBrawler({
          name: brawler.name,
          slug: brawler.slug,
          imagePath: brawler.imagePath,
        });
      }
      setInputValue('');
      setSelectedSlug(null);
      setSuggestions([]);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : strings.errors.generic);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      console.warn('Clipboard API unavailable.');
      return;
    }
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (!url) {
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
    } catch (copyError) {
      console.error('Unable to copy link', copyError);
    }
  };

  const closeModal = () => setShowEndModal(false);

  useEffect(() => {
    if (status === 'won') {
      const winningGuess = [...guesses].reverse().find((guess) => guess.isCorrect);
      if (winningGuess) {
        setRevealedBrawler({
          name: winningGuess.brawler.name,
          slug: winningGuess.brawler.slug,
          imagePath: winningGuess.brawler.imagePath,
        });
      }
    } else if (status === 'lost' && !revealedBrawler) {
      fetchReveal();
    }
  }, [fetchReveal, guesses, revealedBrawler, status]);

  useEffect(() => {
    if (status !== 'playing') {
      setShowEndModal(true);
    }
  }, [status]);

  useEffect(() => {
    if (!linkCopied || typeof window === 'undefined') {
      return;
    }
    const timeout = window.setTimeout(() => setLinkCopied(false), 2000);
    return () => clearTimeout(timeout);
  }, [linkCopied]);

  const statusMessage = (() => {
    if (hydrating) {
      return strings.app.status.restoring;
    }
    if (status === 'won') {
      return strings.app.status.won;
    }
    if (status === 'lost') {
      return strings.app.status.lost;
    }
    return strings.app.status.remaining(remainingAttempts);
  })();

  const shareResults = guesses.map((guess) => guess.result);
  const hasGuesses = guesses.length > 0;

  const showInlineReveal = status !== 'playing' && revealedBrawler;

  return (
    <>
      <header className="text-center">
        <img className="brawl-logo brawl-logo--game" src="/brawdle.png" alt="Brawdle" />
        <p className="mt-2 text-sm text-white">{strings.app.description(meta.maxGuesses)}</p>
      </header>

      {showInlineReveal && (
        <div className="flex flex-col items-center text-center">
          {revealedBrawler.imagePath ? (
            <img
              src={revealedBrawler.imagePath}
              alt={revealedBrawler.name}
              className="h-32 w-32 rounded-3xl border border-amber-200/30 object-cover shadow-lg shadow-amber-500/10"
            />
          ) : (
            <div className="flex h-32 w-32 items-center justify-center rounded-3xl border border-amber-200/30 bg-amber-500/10 text-3xl font-bold text-white">
              {revealedBrawler.name.slice(0, 1)}
            </div>
          )}
          <p className="mt-3 text-sm text-white">
            {strings.endModal.revealInline}{' '}
            <span className="font-semibold text-white">{revealedBrawler.name}</span>
          </p>
        </div>
      )}

      <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <AutocompleteInput
          value={inputValue}
          suggestions={suggestions}
          loading={suggestionsLoading}
          disabled={inputDisabled}
          label={strings.form.inputLabel}
          placeholder={strings.form.placeholder}
          loadingText={strings.autocomplete.loading}
          emptyText={strings.autocomplete.empty}
          onChange={(value) => {
            setInputValue(value);
            setSelectedSlug(null);
          }}
          onSelect={(suggestion) => {
            setInputValue(suggestion.name);
            setSelectedSlug(suggestion.slug);
            setSuggestions([]);
            handleSubmit({ slug: suggestion.slug, name: suggestion.name });
          }}
          onSubmit={() => handleSubmit()}
        />
        <button
          type="button"
          className="brawl-cta mt-4 w-full rounded-2xl px-4 py-3 text-2xl font-semibold disabled:cursor-not-allowed disabled:opacity-60"
          onClick={() => handleSubmit()}
          disabled={inputDisabled}
        >
          <span className="brawl-cta-text" data-text={submitLabel}>
            {submitLabel}
          </span>
        </button>
        {error && <p className="mt-3 text-sm text-white">{error}</p>}
        <p className="mt-3 text-sm text-white">{statusMessage}</p>
      </section>

      <section className="flex flex-col gap-3">
        {guesses.length === 0 && !hydrating && (
          <p className="text-center text-sm text-white">{strings.app.emptyGuesses}</p>
        )}
        {[...guesses.entries()].reverse().map(([index, guess]) => (
          <GuessRow
            key={`${guess.brawler.slug}-${index}`}
            brawler={guess.brawler}
            result={guess.result}
            attributesOrder={meta.attributesOrder}
            attributeLabels={strings.guessRow.attributeLabels}
            showReleasedHint={guesses.length >= 2}
          />
        ))}
      </section>

      {hasGuesses && (
        <div className="space-y-2">
          <ShareButton
            puzzleId={meta.puzzleId}
            results={shareResults}
            status={status}
            maxGuesses={meta.maxGuesses}
            attributesOrder={meta.attributesOrder}
            copyLabel={strings.share.copy}
            copiedLabel={strings.share.copied}
          />
          <p className="text-center text-xs text-white">
            {status === 'playing' ? strings.share.hintPlaying : strings.share.hintFinished}
          </p>
        </div>
      )}

      {showEndModal && status !== 'playing' && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h2 className="modal-title">
              {status === 'won' ? strings.endModal.titleWon : strings.endModal.titleLost}
            </h2>
            <p className="modal-subtitle">
              {status === 'won'
                ? strings.endModal.subtitleWon(guesses.length)
                : strings.endModal.subtitleLost}
            </p>
            {revealedBrawler && (
              <div className="modal-reveal">
                {revealedBrawler.imagePath ? (
                  <img
                    src={revealedBrawler.imagePath}
                    alt={revealedBrawler.name}
                    className="modal-avatar"
                  />
                ) : (
                  <span className="modal-avatar modal-avatar--fallback">
                    {revealedBrawler.name.slice(0, 1)}
                  </span>
                )}
                <div>
                  <p className="modal-reveal-label">{strings.endModal.revealLabel}</p>
                  <p className="modal-reveal-name">{revealedBrawler.name}</p>
                </div>
              </div>
            )}
            {hasGuesses && (
              <>
                <p className="modal-share-hint">{strings.share.invite}</p>
                <ShareButton
                  puzzleId={meta.puzzleId}
                  results={shareResults}
                  status={status}
                  maxGuesses={meta.maxGuesses}
                  attributesOrder={meta.attributesOrder}
                  copyLabel={strings.share.copy}
                  copiedLabel={strings.share.copied}
                />
                <button type="button" className="share-link-button" onClick={handleCopyLink}>
                  {linkCopied ? strings.share.linkCopied : strings.share.copyLink}
                </button>
              </>
            )}
            <button type="button" className="modal-close-button" onClick={closeModal}>
              {strings.endModal.close}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
